import React from "react"

/**
 * Keeps every platter on a `cardRows` table at one shared horizontal scroll
 * offset. Each platter is its own scroll region (that's what lets its frame stay
 * put while the columns move inside it), so without this they would drift out of
 * alignment down the page. Scrolling any one of them moves all of them, and a
 * platter that mounts later — virtualization brings them in as you scroll
 * vertically — adopts the current offset before it paints.
 */
export function usePlatterScrollSync({
  onScrollStateChange,
}: {
  // Reports the shared offset's edge state so pinned columns can mark their
  // inner edge with a divider (see `edgeDividerClasses`).
  onScrollStateChange: (state: {
    scrolledX: boolean
    canScrollRight: boolean
  }) => void
}) {
  const platters = React.useRef(new Map<string, HTMLDivElement>())
  const scrollX = React.useRef(0)
  // Latest callback without re-creating the ref callbacks on every render (they
  // would detach and re-attach every platter, losing its scroll position).
  const report = React.useRef(onScrollStateChange)
  report.current = onScrollStateChange

  const measure = React.useCallback((el: HTMLDivElement) => {
    const overflow = el.scrollWidth - el.clientWidth
    report.current({
      scrolledX: el.scrollLeft > 0,
      canScrollRight: overflow - el.scrollLeft > 1,
    })
  }, [])

  // Ref callbacks, cached per key. A fresh closure each render would make React
  // detach and re-attach every platter on every render — re-running the scroll
  // and measure work below each time.
  const refCallbacks = React.useRef(
    new Map<string, (el: HTMLDivElement | null) => void>(),
  )

  /** Ref callback for a platter, keyed so a detach can find its entry. */
  const registerPlatter = React.useCallback(
    (key: string) => {
      const cached = refCallbacks.current.get(key)
      if (cached) {
        return cached
      }
      const callback = (el: HTMLDivElement | null) => {
        if (!el) {
          platters.current.delete(key)
          return
        }
        platters.current.set(key, el)
        // Adopt the shared offset before the first paint so a platter scrolled
        // in from off-screen doesn't flash at the start of its columns.
        el.scrollLeft = scrollX.current
        measure(el)
      }
      refCallbacks.current.set(key, callback)
      return callback
    },
    [measure],
  )

  const handlePlatterScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const source = event.currentTarget
      const next = source.scrollLeft
      // Echo guard: writing `scrollLeft` below fires each target's own scroll
      // event, which arrives here already matching the stored offset.
      if (next === scrollX.current) {
        return
      }
      scrollX.current = next
      for (const el of platters.current.values()) {
        if (el !== source && el.scrollLeft !== next) {
          el.scrollLeft = next
        }
      }
      measure(source)
    },
    [measure],
  )

  /** Re-measure on layout changes (container resize, column set changes). */
  const remeasurePlatters = React.useCallback(() => {
    const first = platters.current.values().next().value
    if (first) {
      measure(first)
    }
  }, [measure])

  return { registerPlatter, handlePlatterScroll, remeasurePlatters }
}
